import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@components/common/Button';
import { Loading } from '@components/common/Loading';
import { CharacterPreview } from '@components/casting/CharacterPreview';
import { WorkflowSteps } from '@components/workflow/WorkflowSteps';
import { useGetProjectQuery } from '@services/projectsApi';
import { useGetActorsQuery } from '@services/actorsApi';
import { useGetProjectBindingsQuery, useCreateBindingMutation, } from '@services/castingApi';
import { useAutoMatchActorsMutation, useExtractRolesMutation } from '@services/roleMatchingApi';
import { useToast } from '@hooks/useToast';
export default function CastingStudioPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { success: showSuccess, error: showError } = useToast();
    const { data: project, isLoading: projectLoading } = useGetProjectQuery(id);
    const { data: actors = [], isLoading: actorsLoading } = useGetActorsQuery();
    const { data: bindings = [] } = useGetProjectBindingsQuery(id);
    const [createBinding, { isLoading: isBinding }] = useCreateBindingMutation();
    const [autoMatch, { isLoading: isMatching }] = useAutoMatchActorsMutation();
    const [extractRoles, { isLoading: isExtracting }] = useExtractRolesMutation();
    const [selectedRole, setSelectedRole] = useState(null);
    const [selectedActor, setSelectedActor] = useState(null);
    const [genderFilter, setGenderFilter] = useState('');
    const [minAge, setMinAge] = useState('');
    const [maxAge, setMaxAge] = useState('');
    const [showScript, setShowScript] = useState(true);
    const [autoMatchedRoles, setAutoMatchedRoles] = useState(new Map());
    const [roleCharacteristics, setRoleCharacteristics] = useState(new Map());
    const [aiExtractedRoles, setAiExtractedRoles] = useState([]);
    const [useAiRoles, setUseAiRoles] = useState(false);
    const [makeupConfig, setMakeupConfig] = useState({
        skinTone: 50,
        hairStyle: '短发',
        hairColor: '#000000',
        lipstickColor: '#FF6B9D',
        eyeShadowColor: '#8B7355',
        blushIntensity: 50,
    });
    // 转义正则表达式特殊字符
    const escapeRegExp = (str) => {
        return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    };
    const extractRoleDescription = (roleName, content) => {
        const escapedRoleName = escapeRegExp(roleName);
        const descPatterns = [
            new RegExp(`${escapedRoleName}[，,]([^。！？\\n]{10,50})[。！？]`, 'g'),
            new RegExp(`${escapedRoleName}是([^。！？\\n]{5,50})[。！？]`, 'g'),
        ];
        for (const pattern of descPatterns) {
            const match = pattern.exec(content);
            if (match) {
                return match[1].trim();
            }
        }
        return '剧本角色';
    };
    const roles = useMemo(() => {
        // 如果使用AI识别的角色，直接返回
        if (useAiRoles && aiExtractedRoles.length > 0) {
            return aiExtractedRoles;
        }
        if (!project?.script?.content)
            return [];
        const content = project.script.content;
        const roleMap = new Map();
        // ========== 方案1：标准剧本格式识别 ==========
        // 1. **角色名**：对话内容  （剧本对话格式）
        // 2. 【角色名】动作描述     （动作描述格式）
        // 3. - **角色名**（描述）   （人物列表格式）
        // 先识别人物列表中的角色（最准确）
        const characterListPattern = /-\s*\*\*([^*]+)\*\*\s*[（(]([^)）]*?[男女][^)）]*?)[)）]/g;
        let match;
        while ((match = characterListPattern.exec(content)) !== null) {
            const roleName = match[1].trim();
            const roleDescription = match[2].trim();
            if (roleName.length >= 2 && roleName.length <= 10 && !/^\d+$/.test(roleName)) {
                if (!roleMap.has(roleName)) {
                    roleMap.set(roleName, {
                        id: `role-${roleMap.size + 1}`,
                        name: roleName,
                        description: roleDescription,
                        appearances: []
                    });
                }
            }
        }
        // 识别对话格式的角色
        const dialoguePattern = /\*\*([^*]+)\*\*\s*[：:]/g;
        while ((match = dialoguePattern.exec(content)) !== null) {
            const roleName = match[1].trim();
            // 基础过滤
            if (roleName.length > 10 ||
                roleName.length < 2 ||
                /^\d+$/.test(roleName) ||
                /^(剧本名称|类型|时长|人物|风格|主题|简介|标签|场景|地点|时间|画面|镜头|旁白|标题|字幕|剧终|完|结束)/.test(roleName)) {
                continue;
            }
            // 检查冒号后是否有对话内容
            const afterColon = content.substring(match.index + match[0].length, match.index + match[0].length + 50).trim();
            if (!afterColon || /^[\s\n]*\*\*/.test(afterColon)) {
                continue;
            }
            if (!roleMap.has(roleName)) {
                roleMap.set(roleName, {
                    id: `role-${roleMap.size + 1}`,
                    name: roleName,
                    description: extractRoleDescription(roleName, content),
                    appearances: []
                });
            }
            const role = roleMap.get(roleName);
            const contextStart = Math.max(0, match.index - 50);
            const contextEnd = Math.min(content.length, match.index + 150);
            role.appearances.push({
                start: match.index,
                end: match.index + match[0].length,
                context: content.substring(contextStart, contextEnd)
            });
        }
        // 识别动作描述格式
        const actionPattern = /【([^】]+)】/g;
        while ((match = actionPattern.exec(content)) !== null) {
            const roleName = match[1].trim();
            if (roleName.length >= 2 &&
                roleName.length <= 10 &&
                !/^\d+$/.test(roleName) &&
                !/^(场景|镜头|画面|旁白|字幕)/.test(roleName)) {
                if (!roleMap.has(roleName)) {
                    roleMap.set(roleName, {
                        id: `role-${roleMap.size + 1}`,
                        name: roleName,
                        description: extractRoleDescription(roleName, content),
                        appearances: []
                    });
                }
                const role = roleMap.get(roleName);
                const contextStart = Math.max(0, match.index - 50);
                const contextEnd = Math.min(content.length, match.index + 150);
                role.appearances.push({
                    start: match.index,
                    end: match.index + match[0].length,
                    context: content.substring(contextStart, contextEnd)
                });
            }
        }
        // ========== 方案2：叙事格式识别（引号对话） ==========
        // 识别 "对话内容。"她说。 或 男人说："对话内容。"
        // 常见的叙事角色代词
        const narrativeRoles = ['他', '她', '男人', '女人', '男孩', '女孩', '老人', '少年', '少女'];
        // 模式1: "对话"角色说/问/答
        const narrativePattern1 = /"[^"]+[。！？]?"?\s*([他她男女][^。！？，\s]{0,3})(说|问|答|道|笑|叹|喊|低声说|轻声说|冷笑|怒道)/g;
        while ((match = narrativePattern1.exec(content)) !== null) {
            let roleName = match[1].trim();
            // 扩展角色名（如"男人"、"女人"）
            if (narrativeRoles.includes(roleName) || /^[男女](人|孩|子)$/.test(roleName)) {
                if (!roleMap.has(roleName)) {
                    roleMap.set(roleName, {
                        id: `role-${roleMap.size + 1}`,
                        name: roleName,
                        description: `叙事角色：${roleName}`,
                        appearances: []
                    });
                }
                const role = roleMap.get(roleName);
                const contextStart = Math.max(0, match.index - 100);
                const contextEnd = Math.min(content.length, match.index + 100);
                role.appearances.push({
                    start: match.index,
                    end: match.index + match[0].length,
                    context: content.substring(contextStart, contextEnd)
                });
            }
        }
        // 模式2: 角色说："对话"
        const narrativePattern2 = /([他她男女][^。！？，\s]{0,3})(说|问|答|道|笑|叹|喊|低声说|轻声说|冷笑|怒道)\s*[：:]\s*"/g;
        while ((match = narrativePattern2.exec(content)) !== null) {
            let roleName = match[1].trim();
            if (narrativeRoles.includes(roleName) || /^[男女](人|孩|子)$/.test(roleName)) {
                if (!roleMap.has(roleName)) {
                    roleMap.set(roleName, {
                        id: `role-${roleMap.size + 1}`,
                        name: roleName,
                        description: `叙事角色：${roleName}`,
                        appearances: []
                    });
                }
                const role = roleMap.get(roleName);
                const contextStart = Math.max(0, match.index - 100);
                const contextEnd = Math.min(content.length, match.index + 100);
                role.appearances.push({
                    start: match.index,
                    end: match.index + match[0].length,
                    context: content.substring(contextStart, contextEnd)
                });
            }
        }
        // 过滤出现次数太少的角色
        const filteredRoles = Array.from(roleMap.values()).filter(role => {
            // 人物列表中的角色，即使只出现一次也保留
            if (role.description && /[男女].*\d{1,2}[岁]?/.test(role.description)) {
                return true;
            }
            // 其他角色至少要出现2次
            return role.appearances.length >= 2;
        });
        return filteredRoles;
    }, [project?.script?.content, useAiRoles, aiExtractedRoles]);
    const highlightedScript = useMemo(() => {
        if (!project?.script?.content || !selectedRole) {
            return [];
        }
        const content = project.script.content;
        const parts = [];
        let lastIndex = 0;
        const sortedAppearances = [...selectedRole.appearances].sort((a, b) => a.start - b.start);
        sortedAppearances.forEach(appearance => {
            if (appearance.start > lastIndex) {
                parts.push({
                    text: content.substring(lastIndex, appearance.start),
                    highlight: false
                });
            }
            const contextStart = Math.max(lastIndex, appearance.start - 30);
            const contextEnd = Math.min(content.length, appearance.end + 100);
            parts.push({
                text: content.substring(contextStart, contextEnd),
                highlight: true
            });
            lastIndex = contextEnd;
        });
        if (lastIndex < content.length) {
            parts.push({
                text: content.substring(lastIndex),
                highlight: false
            });
        }
        return parts;
    }, [project?.script?.content, selectedRole]);
    const filteredActors = actors.filter((actor) => {
        if (genderFilter && actor.gender !== genderFilter)
            return false;
        if (minAge && actor.age < parseInt(minAge))
            return false;
        if (maxAge && actor.age > parseInt(maxAge))
            return false;
        return true;
    });
    const getRoleBinding = (roleId) => {
        return bindings.find((b) => b.roleId === roleId);
    };
    // 页面加载后自动执行AI匹配
    useEffect(() => {
        const autoMatchOnLoad = async () => {
            // 只在角色加载完成且还没有匹配结果时执行
            if (!id || roles.length === 0 || actors.length === 0)
                return;
            if (autoMatchedRoles.size > 0 || isMatching)
                return;
            try {
                const rolesData = roles.map(role => ({
                    id: role.id,
                    name: role.name,
                    description: role.description,
                    context: role.appearances.map(a => a.context).join('\n')
                }));
                const result = await autoMatch({
                    projectId: id,
                    roles: rolesData
                }).unwrap();
                // 保存角色特征
                const characteristics = new Map();
                result.roleCharacteristics.forEach(rc => {
                    characteristics.set(rc.roleId, rc.tags);
                });
                setRoleCharacteristics(characteristics);
                // 保存匹配结果
                const matches = new Map();
                result.matches.forEach(match => {
                    matches.set(match.roleId, match.actorId);
                });
                setAutoMatchedRoles(matches);
                console.log(`自动匹配完成：为 ${result.matches.length} 个角色推荐了演员`);
            }
            catch (error) {
                console.error('自动匹配失败:', error);
            }
        };
        autoMatchOnLoad();
    }, [roles.length, actors.length, id]); // 依赖角色和演员数据
    // 处理角色点击 - 如果已绑定演员，自动选中该演员
    const handleRoleClick = (role) => {
        setSelectedRole(role);
        // 检查该角色是否已绑定演员
        const binding = getRoleBinding(role.id);
        if (binding) {
            // 找到绑定的演员
            const boundActor = actors.find(a => a.id === binding.actorId);
            if (boundActor) {
                setSelectedActor(boundActor);
                // 如果有保存的妆造配置，加载它
                if (binding.makeupConfig) {
                    const config = binding.makeupConfig;
                    setMakeupConfig({
                        skinTone: config.skinTone || 50,
                        hairStyle: config.hairStyle || '短发',
                        hairColor: config.hairColor || '#000000',
                        lipstickColor: config.lipstickColor || '#FF6B9D',
                        eyeShadowColor: config.eyeShadowColor || '#8B7355',
                        blushIntensity: config.blushIntensity || 50,
                    });
                }
            }
        }
    };
    // 自动匹配演员
    const handleAutoMatch = async () => {
        if (!id || roles.length === 0)
            return;
        try {
            const rolesData = roles.map(role => ({
                id: role.id,
                name: role.name,
                description: role.description,
                context: role.appearances.map(a => a.context).join('\n')
            }));
            const result = await autoMatch({
                projectId: id,
                roles: rolesData
            }).unwrap();
            // 保存角色特征
            const characteristics = new Map();
            result.roleCharacteristics.forEach(rc => {
                characteristics.set(rc.roleId, rc.tags);
            });
            setRoleCharacteristics(characteristics);
            // 保存匹配结果
            const matches = new Map();
            result.matches.forEach(match => {
                matches.set(match.roleId, match.actorId);
            });
            setAutoMatchedRoles(matches);
            // 自动创建绑定
            let successCount = 0;
            for (const match of result.matches) {
                const role = roles.find(r => r.id === match.roleId);
                const actor = actors.find(a => a.id === match.actorId);
                if (role && actor) {
                    try {
                        await createBinding({
                            projectId: id,
                            roleId: role.id,
                            roleName: role.name,
                            actorId: actor.id,
                            makeupConfig,
                        }).unwrap();
                        successCount++;
                    }
                    catch (error) {
                        console.error(`绑定失败: ${role.name} -> ${actor.name}`, error);
                    }
                }
            }
            showSuccess(`AI自动匹配完成，成功绑定 ${successCount} 个角色`);
        }
        catch (error) {
            showError('自动匹配失败，请重试');
            console.error('自动匹配错误:', error);
        }
    };
    // AI识别角色
    const handleAiExtractRoles = async () => {
        if (!project?.script?.content) {
            showError('没有剧本内容');
            return;
        }
        try {
            const result = await extractRoles({
                scriptContent: project.script.content
            }).unwrap();
            if (result.success && result.roles.length > 0) {
                // 转换AI识别的角色为Role格式
                const extractedRoles = result.roles.map((role, index) => ({
                    id: `ai-role-${index + 1}`,
                    name: role.name,
                    description: `${role.description} (${role.gender === 'male' ? '男' : role.gender === 'female' ? '女' : '未知'}, ${role.age}岁)`,
                    appearances: [{
                            start: 0,
                            end: 0,
                            context: role.context
                        }]
                }));
                setAiExtractedRoles(extractedRoles);
                setUseAiRoles(true);
                showSuccess(`AI识别成功，找到 ${extractedRoles.length} 个角色`);
            }
            else {
                showError('未识别到角色');
            }
        }
        catch (error) {
            showError('AI识别失败，请重试');
            console.error('AI识别错误:', error);
        }
    };
    // 计算角色和演员的匹配度
    const calculateMatchScore = (role, actor) => {
        const roleTagsSet = new Set(roleCharacteristics.get(role.id) || []);
        const actorTagsSet = new Set(actor.tags);
        let matchCount = 0;
        roleTagsSet.forEach(tag => {
            if (actorTagsSet.has(tag)) {
                matchCount++;
            }
        });
        const totalTags = Math.max(roleTagsSet.size, actorTagsSet.size);
        return totalTags > 0 ? (matchCount / totalTags) * 100 : 0;
    };
    const handleBindActor = async () => {
        if (!selectedRole || !selectedActor || !id)
            return;
        try {
            await createBinding({
                projectId: id,
                roleId: selectedRole.id,
                roleName: selectedRole.name,
                actorId: selectedActor.id,
                makeupConfig,
            }).unwrap();
            showSuccess(`已将 ${selectedActor.name} 绑定到 ${selectedRole.name}`);
            setSelectedRole(null);
            setSelectedActor(null);
        }
        catch (error) {
            showError('绑定失败，请重试');
        }
    };
    // 保存妆造配置
    const handleSaveMakeup = async () => {
        if (!selectedRole || !selectedActor || !id) {
            showError('请先选择角色和演员');
            return;
        }
        // 检查是否已有绑定
        const binding = getRoleBinding(selectedRole.id);
        if (!binding) {
            showError('请先绑定演员，然后再保存妆造配置');
            return;
        }
        try {
            // 更新绑定的妆造配置
            await createBinding({
                projectId: id,
                roleId: selectedRole.id,
                roleName: selectedRole.name,
                actorId: selectedActor.id,
                makeupConfig,
            }).unwrap();
            showSuccess('妆造配置已保存');
        }
        catch (error) {
            showError('保存失败，请重试');
        }
    };
    const handleNext = () => {
        if (!id)
            return;
        navigate(`/project/${id}/storyboard`);
    };
    if (projectLoading || actorsLoading) {
        return _jsx(Loading, { fullScreen: true, text: "\u52A0\u8F7D\u4E2D..." });
    }
    if (!project) {
        return (_jsxs("div", { className: "flex min-h-[60vh] flex-col items-center justify-center", children: [_jsx("h2", { className: "mb-4 text-2xl font-semibold text-gray-800", children: "\u9879\u76EE\u4E0D\u5B58\u5728" }), _jsx(Button, { onClick: () => navigate('/topics'), children: "\u8FD4\u56DE\u9009\u9898" })] }));
    }
    return (_jsxs("div", { className: "min-h-screen bg-dark-700", children: [_jsx("div", { className: "border-b border-gray-800 bg-dark-600", children: _jsx(WorkflowSteps, { projectId: id, currentStep: "casting" }) }), _jsx("div", { className: "border-b border-gray-800 bg-dark-600 px-6 py-4", children: _jsxs("div", { className: "mx-auto flex max-w-[1600px] items-center justify-between", children: [_jsxs("div", { children: [_jsxs("h1", { className: "text-2xl font-bold text-gray-100", children: [project.title, " - \u9009\u89D2\u4E0E\u5986\u9020"] }), _jsxs("p", { className: "mt-1 text-sm text-gray-400", children: [useAiRoles ? '🤖 AI智能识别' : '📝 正则表达式识别', " \u00B7 \u8BC6\u522B\u5230 ", roles.length, " \u4E2A\u89D2\u8272 \u00B7 \u4E3A\u89D2\u8272\u9009\u62E9\u5408\u9002\u7684\u6F14\u5458\u5E76\u8C03\u6574\u5986\u9020"] })] }), _jsxs("div", { className: "flex items-center gap-3", children: [_jsx(Button, { variant: "secondary", size: "sm", onClick: handleAiExtractRoles, loading: isExtracting, disabled: !project?.script?.content, children: "\uD83D\uDD0D AI\u8BC6\u522B\u89D2\u8272" }), useAiRoles && (_jsx(Button, { variant: "ghost", size: "sm", onClick: () => setUseAiRoles(false), children: "\u4F7F\u7528\u6B63\u5219\u8BC6\u522B" })), _jsx(Button, { variant: "primary", size: "sm", onClick: handleAutoMatch, loading: isMatching, disabled: roles.length === 0, children: "\uD83E\uDD16 AI\u81EA\u52A8\u5339\u914D" }), _jsx(Button, { variant: "ghost", size: "sm", onClick: () => setShowScript(!showScript), children: showScript ? '隐藏剧本' : '显示剧本' }), _jsx(Button, { variant: "ghost", onClick: () => navigate(`/project/${id}/script`), children: "\u2190 \u4E0A\u4E00\u6B65" }), _jsx(Button, { variant: "primary", onClick: handleNext, children: "\u4E0B\u4E00\u6B65\uFF1A\u5206\u955C\u8BBE\u8BA1" })] })] }) }), _jsx("div", { className: "mx-auto max-w-[1600px] p-6", children: _jsxs("div", { className: "grid grid-cols-12 gap-6", children: [_jsx("div", { className: "col-span-3", children: _jsxs("div", { className: "rounded-lg bg-dark-600 p-6 shadow-card border border-gray-800 sticky top-6", children: [_jsx("h2", { className: "mb-4 text-lg font-semibold text-gray-100", children: "\u6F14\u5458\u5E93" }), _jsxs("div", { className: "mb-4 space-y-3", children: [_jsxs("div", { children: [_jsx("label", { className: "mb-1 block text-sm font-medium text-gray-300", children: "\u6027\u522B" }), _jsxs("select", { value: genderFilter, onChange: (e) => setGenderFilter(e.target.value), className: "w-full rounded-lg border border-gray-700 bg-dark-500 px-3 py-2 text-sm text-gray-200 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500", children: [_jsx("option", { value: "", children: "\u5168\u90E8" }), _jsx("option", { value: "male", children: "\u7537" }), _jsx("option", { value: "female", children: "\u5973" })] })] }), _jsxs("div", { children: [_jsx("label", { className: "mb-1 block text-sm font-medium text-gray-300", children: "\u5E74\u9F84\u8303\u56F4" }), _jsxs("div", { className: "flex items-center gap-2", children: [_jsx("input", { type: "number", placeholder: "\u6700\u5C0F", value: minAge, onChange: (e) => setMinAge(e.target.value), className: "w-full rounded-lg border border-gray-700 bg-dark-500 px-3 py-2 text-sm text-gray-200 placeholder-gray-500 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500" }), _jsx("span", { className: "text-gray-500", children: "-" }), _jsx("input", { type: "number", placeholder: "\u6700\u5927", value: maxAge, onChange: (e) => setMaxAge(e.target.value), className: "w-full rounded-lg border border-gray-700 bg-dark-500 px-3 py-2 text-sm text-gray-200 placeholder-gray-500 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500" })] })] })] }), _jsx("div", { className: "space-y-3 max-h-[600px] overflow-y-auto pr-2", children: filteredActors.map((actor) => (_jsx("div", { onClick: () => setSelectedActor(actor), className: `cursor-pointer rounded-lg border-2 p-3 transition-all ${selectedActor?.id === actor.id
                                                ? 'border-primary-500 bg-primary-500/10'
                                                : 'border-gray-700 hover:border-gray-600 bg-dark-500'}`, children: _jsxs("div", { className: "flex items-center gap-3", children: [_jsx("img", { src: actor.imageUrl, alt: actor.name, className: "h-16 w-16 rounded-lg object-cover" }), _jsxs("div", { className: "flex-1 min-w-0", children: [_jsx("h3", { className: "font-medium text-gray-100 truncate", children: actor.name }), _jsxs("p", { className: "text-sm text-gray-400", children: [actor.gender === 'male' ? '男' : actor.gender === 'female' ? '女' : '其他', " \u00B7 ", actor.age, "\u5C81"] }), _jsx("div", { className: "mt-1 flex flex-wrap gap-1", children: actor.tags.slice(0, 2).map((tag) => (_jsx("span", { className: "rounded bg-gray-700 px-2 py-0.5 text-xs text-gray-300", children: tag }, tag))) }), selectedRole && (_jsx("div", { className: "mt-2", children: _jsxs("div", { className: "flex items-center gap-2", children: [_jsx("span", { className: "text-xs text-gray-400", children: "\u5339\u914D\u5EA6:" }), _jsx("div", { className: "flex-1 h-1.5 bg-gray-700 rounded-full overflow-hidden", children: _jsx("div", { className: "h-full bg-gradient-to-r from-blue-500 to-green-500 transition-all", style: { width: `${calculateMatchScore(selectedRole, actor)}%` } }) }), _jsxs("span", { className: "text-xs font-medium text-gray-300", children: [Math.round(calculateMatchScore(selectedRole, actor)), "%"] })] }) }))] })] }) }, actor.id))) })] }) }), _jsxs("div", { className: "col-span-6", children: [_jsxs("div", { className: "mb-6 rounded-lg bg-dark-600 p-6 shadow-card border border-gray-800", children: [_jsxs("h2", { className: "mb-4 text-lg font-semibold text-gray-100 flex items-center gap-2", children: [_jsx("svg", { className: "h-5 w-5 text-primary-400", fill: "none", viewBox: "0 0 24 24", stroke: "currentColor", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" }) }), "\u89D2\u8272\u5217\u8868", _jsx("span", { className: "ml-auto text-sm font-normal text-gray-400", children: "\u70B9\u51FB\u89D2\u8272\u67E5\u770B\u5728\u5267\u672C\u4E2D\u7684\u51FA\u73B0\u4F4D\u7F6E" })] }), roles.length === 0 ? (_jsxs("div", { className: "text-center py-12", children: [_jsx("svg", { className: "mx-auto h-16 w-16 text-gray-600 mb-4", fill: "none", viewBox: "0 0 24 24", stroke: "currentColor", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 1, d: "M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" }) }), _jsx("h3", { className: "text-lg font-semibold text-gray-300 mb-2", children: "\u672A\u8BC6\u522B\u5230\u89D2\u8272" }), _jsx("p", { className: "text-sm text-gray-400 mb-4 max-w-md mx-auto", children: "\u8BF7\u5728\u5267\u672C\u4E2D\u4F7F\u7528\u4EE5\u4E0B\u683C\u5F0F\u6807\u8BB0\u89D2\u8272\u5BF9\u8BDD\uFF1A" }), _jsxs("div", { className: "bg-dark-500 rounded-lg p-4 max-w-md mx-auto text-left border border-gray-700", children: [_jsx("p", { className: "text-xs text-gray-400 mb-2", children: "\u652F\u6301\u7684\u683C\u5F0F\uFF1A" }), _jsxs("div", { className: "space-y-2 text-sm", children: [_jsxs("div", { className: "flex items-start gap-2", children: [_jsx("span", { className: "text-green-400", children: "\u2713" }), _jsx("code", { className: "text-gray-300", children: "**\u89D2\u8272\u540D**\uFF1A\u5BF9\u8BDD\u5185\u5BB9" })] }), _jsxs("div", { className: "flex items-start gap-2", children: [_jsx("span", { className: "text-green-400", children: "\u2713" }), _jsx("code", { className: "text-gray-300", children: "\u3010\u89D2\u8272\u540D\u3011\u52A8\u4F5C\u63CF\u8FF0" })] })] }), _jsxs("div", { className: "mt-3 pt-3 border-t border-gray-700", children: [_jsx("p", { className: "text-xs text-gray-500", children: "\u793A\u4F8B\uFF1A" }), _jsx("pre", { className: "text-xs text-gray-400 mt-1 whitespace-pre-wrap", children: "**\u674E\u660E**\uFF1A\u4F60\u597D\uFF0C\u5F88\u9AD8\u5174\u8BA4\u8BC6\u4F60\u3002 \u3010\u674E\u660E\u3011\u8D70\u8FDB\u623F\u95F4\uFF0C\u73AF\u987E\u56DB\u5468\u3002" })] })] }), _jsx(Button, { variant: "ghost", size: "sm", className: "mt-4", onClick: () => navigate(`/project/${id}/script`), children: "\u8FD4\u56DE\u7F16\u8F91\u5267\u672C" })] })) : (_jsx("div", { className: "grid grid-cols-2 gap-3 relative", children: roles.map((role) => {
                                                const binding = getRoleBinding(role.id);
                                                const boundActor = binding ? actors.find(a => a.id === binding.actorId) : null;
                                                const isSelected = selectedRole?.id === role.id;
                                                return (_jsx("div", { className: "relative", children: _jsxs("div", { onClick: () => handleRoleClick(role), className: `cursor-pointer rounded-lg border-2 p-4 transition-all relative ${isSelected
                                                            ? 'border-primary-500 bg-primary-500/10'
                                                            : 'border-gray-700 hover:border-gray-600 bg-dark-500'}`, children: [isSelected && selectedActor && (_jsx("div", { className: "absolute top-2 left-2 right-2 z-10 flex justify-center pointer-events-none", children: _jsx("button", { onClick: (e) => {
                                                                        e.stopPropagation();
                                                                        handleBindActor();
                                                                    }, disabled: isBinding, className: "pointer-events-auto bg-gradient-to-r from-primary-500 to-blue-500 hover:from-primary-600 hover:to-blue-600 text-white text-xs font-medium py-2 px-4 rounded-lg shadow-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50", children: isBinding ? (_jsxs(_Fragment, { children: [_jsxs("svg", { className: "animate-spin h-3 w-3", fill: "none", viewBox: "0 0 24 24", children: [_jsx("circle", { className: "opacity-25", cx: "12", cy: "12", r: "10", stroke: "currentColor", strokeWidth: "4" }), _jsx("path", { className: "opacity-75", fill: "currentColor", d: "M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" })] }), "\u7ED1\u5B9A\u4E2D..."] })) : (_jsxs(_Fragment, { children: [_jsx("svg", { className: "h-3 w-3", fill: "none", viewBox: "0 0 24 24", stroke: "currentColor", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" }) }), "\u7ED1\u5B9A ", selectedActor.name] })) }) })), _jsxs("div", { className: "flex items-start justify-between mb-2", children: [_jsx("h3", { className: "font-medium text-gray-100", children: role.name }), _jsxs("span", { className: "text-xs text-gray-500 bg-gray-800 px-2 py-1 rounded", children: [role.appearances.length, " \u6B21\u51FA\u73B0"] })] }), _jsx("p", { className: "mt-1 text-sm text-gray-400 line-clamp-2", children: role.description }), roleCharacteristics.has(role.id) && (_jsx("div", { className: "mt-2 flex flex-wrap gap-1", children: roleCharacteristics.get(role.id).map((tag) => (_jsx("span", { className: "rounded bg-blue-500/20 px-2 py-0.5 text-xs text-blue-400 border border-blue-500/30", children: tag }, tag))) })), binding && boundActor ? (_jsx("div", { className: "mt-3 rounded-lg bg-green-500/10 p-2 border border-green-500/30", children: _jsxs("div", { className: "flex items-center gap-2", children: [_jsx("img", { src: boundActor.imageUrl, alt: boundActor.name, className: "h-8 w-8 rounded object-cover" }), _jsxs("div", { className: "flex-1 min-w-0", children: [_jsx("p", { className: "font-medium text-green-400 text-xs truncate", children: boundActor.name }), _jsxs("p", { className: "text-xs text-gray-400", children: [boundActor.gender === 'male' ? '男' : '女', " \u00B7 ", boundActor.age, "\u5C81"] })] }), _jsx("svg", { className: "h-4 w-4 text-green-500 flex-shrink-0", fill: "none", viewBox: "0 0 24 24", stroke: "currentColor", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M5 13l4 4L19 7" }) })] }) })) : (_jsx(_Fragment, { children: autoMatchedRoles.has(role.id) ? (_jsx("div", { className: "mt-3 rounded-lg bg-blue-500/10 p-2 border border-blue-500/30", children: _jsxs("div", { className: "flex items-center gap-2", children: [_jsx("svg", { className: "h-4 w-4 text-blue-400", fill: "none", viewBox: "0 0 24 24", stroke: "currentColor", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" }) }), _jsx("span", { className: "text-xs text-blue-400", children: "AI\u63A8\u8350\u5339\u914D" })] }) })) : (_jsx("div", { className: "mt-3 rounded-lg bg-gray-800 p-2 border border-gray-700", children: _jsxs("div", { className: "flex items-center gap-2", children: [_jsx("svg", { className: "h-4 w-4 text-gray-500", fill: "none", viewBox: "0 0 24 24", stroke: "currentColor", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" }) }), _jsx("span", { className: "text-xs text-gray-500", children: "\u672A\u7ED1\u5B9A\u6F14\u5458" })] }) })) }))] }) }, role.id));
                                            }) }))] }), _jsxs("div", { className: "rounded-lg bg-dark-600 p-6 shadow-card border border-gray-800", children: [_jsxs("h2", { className: "mb-4 text-lg font-semibold text-gray-100 flex items-center gap-2", children: [_jsx("svg", { className: "h-5 w-5 text-primary-400", fill: "none", viewBox: "0 0 24 24", stroke: "currentColor", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" }) }), "\u6F14\u5458\u5F62\u8C61\u9884\u89C8\u4E0E\u5986\u9020\u8BBE\u7F6E"] }), _jsx(CharacterPreview, { actor: selectedActor, makeupConfig: makeupConfig, onMakeupChange: setMakeupConfig, onSaveMakeup: handleSaveMakeup, isSaving: isBinding })] })] }), showScript && (_jsx("div", { className: "col-span-3", children: _jsxs("div", { className: "rounded-lg bg-dark-600 p-6 shadow-card border border-gray-800 sticky top-6", children: [_jsxs("h2", { className: "mb-4 text-lg font-semibold text-gray-100 flex items-center gap-2", children: [_jsx("svg", { className: "h-5 w-5 text-primary-400", fill: "none", viewBox: "0 0 24 24", stroke: "currentColor", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" }) }), "\u5267\u672C\u5185\u5BB9"] }), selectedRole && (_jsxs("div", { className: "mb-3 rounded-lg bg-primary-500/10 border border-primary-500/30 p-3", children: [_jsxs("p", { className: "text-sm text-primary-400 font-medium", children: ["\u5DF2\u9009\u4E2D\uFF1A", selectedRole.name] }), _jsxs("p", { className: "text-xs text-gray-400 mt-1", children: ["\u9AD8\u4EAE\u663E\u793A\u8BE5\u89D2\u8272\u5728\u5267\u672C\u4E2D\u7684 ", selectedRole.appearances.length, " \u5904\u51FA\u73B0"] })] })), _jsx("div", { className: "max-h-[700px] overflow-y-auto pr-2 text-sm leading-relaxed", children: selectedRole && highlightedScript.length > 0 ? (_jsx("div", { className: "space-y-1", children: highlightedScript.map((part, index) => (_jsx("span", { className: part.highlight ? 'bg-yellow-500/20 text-yellow-200 px-1 rounded' : 'text-gray-300', children: part.text }, index))) })) : (_jsx("p", { className: "text-gray-300 whitespace-pre-wrap", children: project.script?.content || '暂无剧本内容' })) })] }) }))] }) })] }));
}
